import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Coins } from "lucide-react";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 30;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState("polling");
  const [detail, setDetail] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let count = 0;
    const tick = async () => {
      if (cancelled) return;
      count += 1;
      setAttempts(count);
      try {
        const r = await http.get(`/payments/status/${sessionId}`);
        setDetail(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          // refresh AuthContext user so the navbar credits badge updates
          try { await refreshUser(); } catch (_) {}
          return;
        }
        if (["expired", "failed"].includes(r.data.payment_status)) {
          setStatus("failed"); return;
        }
      } catch (_) {}
      if (count >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
      setTimeout(tick, POLL_INTERVAL);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="payment-success-page">
      {status === "polling" && (
        <>
          <Loader2 className="h-10 w-10 text-emerald-700 mx-auto animate-spin" />
          <h1 className="mt-6 font-display text-2xl font-bold text-slate-900">Confirming your payment...</h1>
          <p className="mt-2 text-slate-600 text-sm">This usually takes just a moment. Attempt {attempts}/{MAX_ATTEMPTS}.</p>
        </>
      )}
      {status === "paid" && (
        <div data-testid="payment-success-confirmed">
          <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
            <Check className="h-7 w-7 text-emerald-700" />
          </div>
          <h1 className="mt-6 font-display text-3xl font-bold text-slate-900">Payment confirmed</h1>
          <p className="mt-2 text-slate-600">
            {detail?.credits_granted
              ? <>You have <b>{detail.credits_granted}</b> new credits.</>
              : "Credits will appear in your account shortly."}
          </p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm text-emerald-800">
            <Coins className="h-4 w-4" /> Current balance: {user?.credits ?? "—"} credits
          </div>
          <div className="mt-8 flex justify-center gap-3">
            <Link to="/dashboard"><Button className="bg-emerald-900 hover:bg-emerald-800 text-white" data-testid="btn-go-dashboard">Go to dashboard</Button></Link>
            <Link to="/templates"><Button variant="outline">Browse templates</Button></Link>
          </div>
        </div>
      )}
      {status === "failed" && (
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Payment did not complete</h1>
          <p className="mt-2 text-slate-600">Your card wasn't charged. Try again from pricing.</p>
          <Link to="/pricing"><Button className="mt-6 bg-emerald-900 hover:bg-emerald-800 text-white">Back to pricing</Button></Link>
        </div>
      )}
      {status === "timeout" && (
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Still processing</h1>
          <p className="mt-2 text-slate-600">Your payment is taking a bit longer. Refresh in a moment, or check your dashboard.</p>
          <Link to="/dashboard"><Button className="mt-6 bg-emerald-900 hover:bg-emerald-800 text-white">Go to dashboard</Button></Link>
        </div>
      )}
      {status === "error" && (
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Missing session</h1>
          <p className="mt-2 text-slate-600">We couldn't find your Stripe session.</p>
          <Link to="/pricing"><Button className="mt-6 bg-emerald-900 hover:bg-emerald-800 text-white">Back to pricing</Button></Link>
        </div>
      )}
    </div>
  );
}
